import { zodResolver } from '@hookform/resolvers/zod';
import { format } from 'date-fns';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useShallow } from 'zustand/react/shallow';
import { IBirth } from '@clickersphere/core/entities/IBirth';
import { ResponseError } from '@clickersphere/core/entities/ResponseError';
import { DeliveryMethodEnum } from '@clickersphere/core/enums/DeliveryMethodEnum';
import { SupportedLanguageEnum } from '@clickersphere/core/enums/SupportedLanguageEnum';
import {
  EN_US_DATE_FORMAT,
  parseDate,
  PT_BR_DATE_FORMAT,
} from '@clickersphere/core/utils/parseDate';
import { useCreateBirth } from '../../../../app/hooks/birth/useCreateBirth';
import { useUpdateBirth } from '../../../../app/hooks/birth/useUpdateBirth';
import { useError } from '../../../../app/hooks/useError';
import useToast from '../../../../app/hooks/useToast';
import { useStore } from '../../../../app/store';
import useLanguageSelectorController from '../../LanguageSelector/useLanguageSelectorController';

interface IUseBirthForm {
  leadId: string;
  birth?: IBirth;
}

export function useBirthFormController({ leadId, birth }: IUseBirthForm) {
  const { handleCloseModal } = useStore(
    useShallow((state) => ({
      handleCloseModal: state.modal.handleCloseModal,
    })),
  );
  const { t } = useTranslation();
  const { toast } = useToast();
  const { handleError } = useError();
  const { selectedLanguage } = useLanguageSelectorController();
  const { createBirthAsync, createBirthIsPending } = useCreateBirth();
  const { updateBirthAsync, updateBirthIsPending } = useUpdateBirth();
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(
    birth?.locationId ?? null,
  );

  const dateFormat =
    selectedLanguage === SupportedLanguageEnum['PT-BR']
      ? PT_BR_DATE_FORMAT
      : EN_US_DATE_FORMAT;

  const schema = z.object({
    expectedBirthDate: z
      .string()
      .optional()
      .refine(
        (value) => {
          if (!value || value.trim() === '') return true;
          return !!parseDate(value, dateFormat);
        },
        { message: t('birth.errors.expectedBirthDateInvalid') },
      ),
    birthDate: z
      .string()
      .optional()
      .refine(
        (value) => {
          if (!value || value.trim() === '') return true;
          return !!parseDate(value, dateFormat);
        },
        { message: t('birth.errors.birthDateInvalid') },
      ),
    babyName: z.string().max(100, t('birth.errors.babyNameTooLong')).optional(),
    deliveryMethod: z.string().optional(),
    photographyWishes: z
      .string()
      .max(2000, t('birth.errors.photographyWishesTooLong'))
      .optional(),
  });

  type FormData = z.input<typeof schema>;

  const {
    handleSubmit: hookFormHandleSubmit,
    register,
    formState: { errors },
    control,
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(schema) as never,
    defaultValues: {
      expectedBirthDate: birth?.expectedBirthDate
        ? format(
            new Date(`${birth.expectedBirthDate.slice(0, 10)}T00:00:00`),
            dateFormat,
          )
        : '',
      birthDate: birth?.birthDate
        ? format(
            new Date(`${birth.birthDate.slice(0, 10)}T00:00:00`),
            dateFormat,
          )
        : '',
      babyName: birth?.babyName ?? '',
      deliveryMethod: birth?.deliveryMethod ?? '',
      photographyWishes: birth?.photographyWishes ?? '',
    },
  });

  function toIsoDateOnly(value?: string): string | null {
    if (!value || value.trim() === '') return null;
    const parsed = parseDate(value, dateFormat);
    if (!parsed) return null;
    return format(parsed, 'yyyy-MM-dd');
  }

  const handleSubmit = hookFormHandleSubmit(async (data) => {
    const payload = {
      locationId: selectedLocationId,
      expectedBirthDate: toIsoDateOnly(data.expectedBirthDate),
      birthDate: toIsoDateOnly(data.birthDate),
      babyName: data.babyName?.trim() ? data.babyName.trim() : null,
      deliveryMethod: data.deliveryMethod
        ? (data.deliveryMethod as DeliveryMethodEnum)
        : null,
      photographyWishes: data.photographyWishes?.trim()
        ? data.photographyWishes.trim()
        : null,
    };

    try {
      if (birth) {
        await updateBirthAsync({ id: birth.id, data: payload });
        toast(t('birth.toast.updated'), 'success');
      } else {
        await createBirthAsync({ leadId, ...payload });
        toast(t('birth.toast.created'), 'success');
      }

      handleCloseModal();
    } catch (error) {
      if (error instanceof ResponseError) {
        handleError(error);
      } else {
        toast(t('birth.toast.error'), 'error');
      }
    }
  });

  return {
    t,
    errors,
    control,
    register,
    isFetching: createBirthIsPending || updateBirthIsPending,
    selectedLocationId,
    setSelectedLocationId,
    handleSubmit,
    handleFormReset: reset,
  };
}
